import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { usePopup } from '@/context/PopupContext';
import { Menu, X, ChevronDown, Phone, MessageCircle } from 'lucide-react';

const servicesMenu = [
  {
    name: 'Trademark Registration',
    image: 'https://images.unsplash.com/photo-1764601841377-2fd86c9fd8bc?q=85&w=400',
    items: [
      'Register Your Trademark',
      'TTAB Petition',
      'Clearance Search Services',
      'Trademark Ownership Transfers',
      'Managed Trademark Renewal',
      'Submission of Statements of Use',
      'Support & Response for Trademark Office Actions',
      'Active Monitoring for Trademark Infringement',
      'Support & Guidance for Trademark Cancellations',
      'Legal Representation for Trademark Disputes',
      'Combatting Counterfeiting & Trademark Infringement'
    ]
  },
  {
    name: 'Copyright Registration',
    image: 'https://images.unsplash.com/photo-1537861295351-76bb831ece99?q=85&w=400',
    items: [
      'Register Your Copyright',
      'Copyright Enforcement Assistance',
      'Copyright Transfers & Licensing Support',
      'Legal Representation for Copyright',
      'Copyright Portfolio Management',
      'DMCA Compliance'
    ]
  },
  {
    name: 'Patent Services',
    image: 'https://images.unsplash.com/photo-1649406458887-2b6561c36a4d?q=85&w=400',
    items: [
      'Existing Patent Search',
      'Response to Patent Office Actions',
      'Patent Application Submission',
      'Legal Representation for Patent Protection'
    ]
  },
  {
    name: 'Company Formation',
    image: 'https://images.unsplash.com/photo-1591522810896-cb5f45acb9a1?q=85&w=400',
    items: [
      'LLC',
      'C Corporation',
      'S Corporation',
      'Non-Profit'
    ]
  },
  {
    name: 'Legal Services',
    image: 'https://images.unsplash.com/photo-1687289133469-b2a07a13b78b?q=85&w=400',
    items: [
      'Legal Consultation on Partnerships & LLPs',
      'Due Diligence',
      'Business Immigration Law',
      'IT & Commercial Law',
      'Data Protection & Privacy Law'
    ]
  },
  {
    name: 'Additional Services',
    image: 'https://images.unsplash.com/photo-1765020553734-2c050ddb9494?q=85&w=400',
    items: [
      'Amazon Brand Registry (US)',
      'Business Formation Services',
      'Company Dissolution',
      'Issue of Shares',
      'Transfer of Shares',
      'Certificate of Good Standing',
      'Company Name Change',
      'Company Renewals',
      'Nominee Director & Secretarial Services',
      'Bookkeeping',
      'Tax Advisory & Filing',
      'CFO Services'
    ]
  }
];

export const Navbar = ({ isLandingPage = false }) => {
  const { openPopup } = usePopup();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [activeService, setActiveService] = useState(0);
  const location = useLocation();
  
  // Add timeout ref for delayed closing
  const closeTimeoutRef = React.useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setServicesOpen(false);
  }, [location]);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    // Clear any pending close timeout
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setServicesOpen(true);
  };

  const handleMouseLeave = () => {
    // Delay closing by 300ms to prevent accidental closes
    closeTimeoutRef.current = setTimeout(() => {
      setServicesOpen(false);
    }, 300);
  };

  const handleChatClick = () => {
    if (window.Tawk_API) {
      window.Tawk_API.maximize();
    }
  };

  const isActive = (path) => location.pathname === path;

  if (isLandingPage) {
    return (
      <>
        {/* Top Bar */}
        <div className="bg-blue-900/30 border-b border-blue-900/20 py-2">
          <div className="container mx-auto px-4 md:px-6 max-w-7xl">
            <div className="flex justify-end items-center gap-6 text-sm">
              <button
                onClick={handleChatClick}
                className="flex items-center gap-2 text-gray-300 hover:text-blue-400 transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                <span>Chat Now</span>
              </button>
              <a href="tel:+18776380328" className="flex items-center gap-2 text-gray-300 hover:text-blue-400 transition-colors">
                <Phone className="w-4 h-4" />
                <span>1-877-638-0328</span>
              </a>
            </div>
          </div>
        </div>
        
        {/* Main Navigation */}
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-brand-navy/95 backdrop-blur-md shadow-lg' : 'bg-brand-navy'
        }`}>
          <div className="container mx-auto px-2 sm:px-4 md:px-6 max-w-7xl">
            <div className="flex items-center justify-between h-16 sm:h-20">
              <Link to="/lp" className="flex items-center flex-shrink-0">
                <img src="/logo-white.png" alt="US Brand Experts" className="h-10 sm:h-12 md:h-14 w-auto" />
              </Link>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <Button
                  onClick={openPopup}
                  data-testid="lp-register-button"
                  size="sm"
                  className="bg-blue-500 hover:bg-blue-600 text-white font-semibold transition-transform hover:scale-105 text-[10px] sm:text-xs md:text-sm px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 whitespace-nowrap"
                >
                  <span className="hidden sm:inline">Register My Trademark</span>
                  <span className="sm:hidden">Register</span>
                </Button>
                <Button
                  onClick={handleChatClick}
                  data-testid="lp-chat-button"
                  size="sm"
                  variant="outline"
                  className="border-blue-500 text-blue-400 hover:bg-blue-500/10 transition-all text-[10px] sm:text-xs md:text-sm px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 whitespace-nowrap"
                >
                  <span className="hidden sm:inline">Chat Now</span>
                  <span className="sm:hidden">Chat</span>
                </Button>
              </div>
            </div>
          </div>
        </nav>
      </>
    );
  }

  return (
    <>
      {/* Top Bar - Fixed position with solid background when scrolled */}
      <div className={`fixed top-0 left-0 right-0 z-50 py-2 transition-all duration-300 ${
        scrolled ? 'bg-brand-navy backdrop-blur-md' : 'bg-blue-900/30'
      }`}>
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex justify-end items-center gap-6 text-sm">
            <button
              onClick={handleChatClick}
              className="flex items-center gap-2 text-gray-300 hover:text-blue-400 transition-colors"
              data-testid="topbar-chat-button"
            >
              <MessageCircle className="w-4 h-4" />
              <span>Chat Now</span>
            </button>
            <a href="tel:+18776380328" className="flex items-center gap-2 text-gray-300 hover:text-blue-400 transition-colors">
              <Phone className="w-4 h-4" />
              <span>1-877-638-0328</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation - Fixed below top bar with no gap */}
      <nav className={`fixed top-[36px] left-0 right-0 z-40 transition-all duration-300 ${
        scrolled ? 'bg-brand-navy backdrop-blur-md shadow-lg' : 'bg-brand-navy'
      } border-b border-blue-900/20`}>
        <div className="container mx-auto px-2 sm:px-4 md:px-6 max-w-7xl">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <Link to="/" className="flex items-center flex-shrink-0">
              <img src="/logo-white.png" alt="US Brand Experts" className="h-10 sm:h-11 md:h-12 w-auto" />
            </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <Link
              to="/"
              className={`text-lg font-medium transition-colors ${
                isActive('/') ? 'text-blue-400' : 'text-white hover:text-blue-400'
              }`}
            >
              Home
            </Link>

            <div
              className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button className="flex items-center gap-1 text-lg font-medium text-white hover:text-blue-400 transition-colors">
                Services
                <ChevronDown className="w-4 h-4" />
              </button>
              {servicesOpen && (
                <div 
                  className="fixed left-0 right-0 top-[140px] bg-brand-navy-card border-t border-b border-blue-900/30 shadow-2xl z-50"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <div className="container mx-auto px-4 md:px-6 max-w-7xl py-8">
                    <div className="grid grid-cols-12 gap-8">
                      {/* Left - Service Categories */}
                      <div className="col-span-3">
                        <h3 className="text-blue-400 font-semibold mb-4 uppercase tracking-wide text-sm">
                          Our Services
                        </h3>
                        <div className="space-y-1">
                          {servicesMenu.map((service, idx) => (
                            <button
                              key={idx}
                              onClick={() => setActiveService(idx)}
                              onMouseEnter={() => setActiveService(idx)}
                              className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                                activeService === idx
                                  ? 'bg-blue-500/20 text-blue-400 border-l-2 border-blue-500'
                                  : 'text-gray-300 hover:bg-blue-500/10 hover:text-white'
                              }`}
                            >
                              {service.name}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Center - Service Items */}
                      <div className="col-span-5">
                        <h3 className="text-white font-semibold mb-4 text-lg">
                          {servicesMenu[activeService].name}
                        </h3>
                        <div className="grid grid-cols-2 gap-2">
                          {servicesMenu[activeService].items.map((item, itemIdx) => (
                            <Link
                              key={itemIdx}
                              to={`/services/${servicesMenu[activeService].name.toLowerCase().replace(/\s+/g, '-')}/${item.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`}
                              className="px-3 py-2 text-sm text-gray-300 hover:bg-blue-500/10 hover:text-blue-400 transition-colors rounded"
                              onClick={() => setServicesOpen(false)}
                            >
                              {item}
                            </Link>
                          ))}
                        </div>
                      </div>

                      {/* Right - Image */}
                      <div className="col-span-4">
                        <img
                          src={servicesMenu[activeService].image}
                          alt={servicesMenu[activeService].name}
                          className="w-full h-64 object-cover rounded-lg shadow-xl"
                        />
                        <div className="mt-4 p-4 bg-blue-500/10 rounded-lg">
                          <p className="text-gray-300 text-sm leading-relaxed">
                            Professional {servicesMenu[activeService].name.toLowerCase()} services to protect and enhance your business.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/about"
              className={`text-lg font-medium transition-colors ${
                isActive('/about') ? 'text-blue-400' : 'text-white hover:text-blue-400'
              }`}
            >
              About Us
            </Link>

            <Link
              to="/contact-us"
              className={`text-lg font-medium transition-colors ${
                isActive('/contact-us') ? 'text-blue-400' : 'text-white hover:text-blue-400'
              }`}
            >
              Contact Us
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="lg:hidden p-2 text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden pb-4 border-t border-blue-900/20 mt-2 max-h-[70vh] overflow-y-auto">
            <div className="py-2">
              <Link
                to="/"
                className={`block px-4 py-3 text-base font-medium ${
                  isActive('/') ? 'text-blue-400' : 'text-white'
                }`}
              >
                Home
              </Link>
              
              <div className="px-4 py-2">
                <div className="font-medium text-white mb-2">Services</div>
                {servicesMenu.map((category, idx) => (
                  <div key={idx} className="mb-3">
                    <div className="font-semibold text-blue-400 text-sm mb-1 uppercase tracking-wide">{category.name}</div>
                    <div className="pl-3 space-y-1">
                      {category.items.map((item, itemIdx) => (
                        <Link
                          key={itemIdx}
                          to={`/services/${category.name.toLowerCase().replace(/\s+/g, '-')}/${item.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`}
                          className="block py-2 text-sm text-gray-300 hover:text-blue-400"
                        >
                          {item}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <Link
                to="/about"
                className={`block px-4 py-3 text-base font-medium ${
                  isActive('/about') ? 'text-blue-400' : 'text-white'
                }`}
              >
                About Us
              </Link>

              <Link
                to="/contact-us"
                className={`block w-full text-left px-4 py-3 text-base font-medium ${
                  isActive('/contact-us') ? 'text-blue-400' : 'text-white'
                }`}
              >
                Contact Us
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
    </>
  );
};